import { useEffect, type RefObject } from 'react';

import { useMagicIslandController } from './MagicIslandContext';
import { useSectionReachesIsland } from './use-section-reaches-island';

const HIDDEN = { kind: 'hidden' } as const;

/**
 * Hard bound for the bottom of the page: hides the island once `ref`'s bottom
 * edge has scrolled up past the island's berth — i.e. the page's final content
 * section is behind us and the footer is entering the island's space. Unlike a
 * reading-line claim (which releases the moment you scroll past a section, so
 * the fallback pill would reappear over the footer), this stays hidden from
 * there to the very bottom, then restores as you scroll back up.
 *
 * Pass the page's last content block as `ref`. No-ops without a controller
 * (feature off) or when `enabled` is false.
 */
export const useIslandHideNearBottom = (
    ref: RefObject<HTMLElement | null>,
    enabled: boolean,
): void => {
    const controller = useMagicIslandController();
    const active = enabled && controller !== null;
    // True while the section still reaches down into the berth; false once it
    // has cleared it (we are level with the footer below it).
    const reaches = useSectionReachesIsland(ref, active);

    useEffect(() => {
        if (!active || !controller) return;
        controller.setOverride(reaches ? null : HIDDEN);
        return () => controller.setOverride(null);
    }, [active, controller, reaches]);
};
